import { AxiosResponse } from 'axios';
import { RegistrationModal, SendMessage } from 'components';
import FormBase from 'components/form-base';
import { Modal, ModalBody, ModalElement, useUtmParams } from 'kl-b2c-ui-kit';
import React, { forwardRef, LegacyRef, useState } from 'react';
import {
    FieldValues,
    FormProvider,
    SubmitHandler,
    useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CSSObject } from 'styled-components';
import { HandbookRegistration, HandbookLanguage } from 'types';
import HandbookRegistrationRows from './HandbookRegistrationRows';
import { SUPPORTED_LANGS } from 'containers/pages/kids-cyber-resilience/components/handbook/Handbook';

export enum CourseRegisterModalType {
    Default = 1,
    Success = 2,
    Error = 3,
}

export type CourseSecondaryModalConfig = {
    styles: CSSObject;
    config?: {
        title: string;
        description: string;
        color?: 'error' | 'primary';
        buttonText: string;
        action: () => void;
    };
};

interface RegisterModalProps {
    register: (data: HandbookRegistration) => Promise<AxiosResponse>;
    onClose?: () => void;
    activeLang: string;
}

const HandbookRegisterModal = forwardRef<ModalElement, RegisterModalProps>(
    ({ register, onClose, activeLang }, ref) => {
        const { t } = useTranslation([
            'pages/courses',
            'common/cooperation-form',
            'translation',
            'common/errors',
        ]);

        const [registrationLoading, setRegistrationLoading] =
            useState<boolean>(false);
        const [doi, setDoi] = useState<string>();

        const [modalType, setModalType] = useState<CourseRegisterModalType>(
            CourseRegisterModalType.Default
        );

        const methods = useForm<FieldValues>();

        const getConfig = (
            type: CourseRegisterModalType
        ): CourseSecondaryModalConfig => {
            switch (type) {
                case CourseRegisterModalType.Error: {
                    return {
                        styles: {
                            background:
                                'linear-gradient(135deg, #FF5D73 5%, #e3bdc1 100%)',
                        },
                        config: {
                            title: t('pages/courses:err-title-send-message'),
                            description: t(
                                'pages/courses:err-description-send-message'
                            ),
                            color: 'error',
                            buttonText: t('pages/courses:button-send-message'),
                            action: () =>
                                (
                                    ref as React.MutableRefObject<ModalElement>
                                )?.current?.close(),
                        },
                    };
                }
                case CourseRegisterModalType.Success: {
                    return {
                        styles: {
                            background:
                                'linear-gradient(154.05deg, #66FE99 6.25%, #A8E6FC 69.87%), radial-gradient(201.26% 196.4% at 100% 0%, #FBFAFE 0%, rgba(255, 255, 255, 0) 100%)',
                        },
                        config: {
                            title: t('pages/courses:title-send-message'),
                            description:
                                doi ||
                                t('pages/courses:description-send-message'),
                            buttonText: t('pages/courses:button-send-message'),
                            action: () =>
                                (
                                    ref as React.MutableRefObject<ModalElement>
                                )?.current?.close(),
                        },
                    };
                }
                default: {
                    return {
                        styles: {
                            backgroundImage: `url(/assets/images/registrations/course_b2b_bg.png)`,
                            minHeight: 858,
                            '@media (max-width: 639px)': {
                                padding: '40px 16px 16px',
                            },
                        },
                    };
                }
            }
        };

        const onSubmit: SubmitHandler<FieldValues> = async (
            data: FieldValues
        ) => {
            setRegistrationLoading(true);
            const utm = useUtmParams();

            let status = 0;

            const languageEnum =
                SUPPORTED_LANGS.find((lang) => lang.id === activeLang)
                    ?.enumValue || HandbookLanguage.English;

            try {
                const requestData: HandbookRegistration = {
                    ...(data as HandbookRegistration),
                    agreement: {
                        ...data.agreement,
                        marketingCheckBoxText: t(
                            'common/cooperation-form:marketingLabel'
                        ),
                        privacyPolicyCheckBoxText: t(
                            'common/cooperation-form:privacyPolicyLabel'
                        ),
                    },
                    language: languageEnum,
                    ...utm,
                };

                const result = await register(requestData);
                status = result.status;
                if (status === 202) setDoi(t('translation:success-confirm'));
                setModalType(CourseRegisterModalType.Success);
            } catch (error) {
                setModalType(CourseRegisterModalType.Error);
            } finally {
                setRegistrationLoading(false);
            }

            return status;
        };

        const modalConfig = getConfig(modalType);

        return (
            <Modal
                ref={ref as LegacyRef<ModalElement>}
                size={'md'}
                outsideClose
                showClose={false}
                styles={{
                    borderRadius: '24px',
                    overflow: 'auto',
                }}
                wrapStyles={{
                    overflow: 'auto',
                }}
                atSelector={'courseRegistrationModal'}
                onClose={onClose}>
                <ModalBody styles={{ ...modalConfig.styles }}>
                    {modalType === CourseRegisterModalType.Default ? (
                        <RegistrationModal
                            title={t('pages/courses:registration-modal-title')}
                            close={() =>
                                (
                                    ref as React.MutableRefObject<ModalElement>
                                )?.current?.close()
                            }>
                            <FormProvider {...methods}>
                                <FormBase
                                    styles={{
                                        width: '100%',
                                        '&:before': { display: 'none' },
                                        padding: '0',
                                        button: {
                                            width: '100%',
                                        },
                                    }}
                                    success={undefined}
                                    loading={registrationLoading}
                                    submit={onSubmit}
                                    resources={{
                                        submitLabel: t('pages/courses:submit'),
                                    }}
                                    policies={[
                                        {
                                            name: 'agreement.privacyPolicyIsSelected',
                                            label: t(
                                                'pages/courses:privacyPolicyLabel'
                                            ),
                                            required: true,
                                        },
                                        {
                                            name: 'agreement.marketingIsSelected',
                                            label: t(
                                                'pages/courses:marketingLabel'
                                            ),
                                        },
                                    ]}>
                                    <HandbookRegistrationRows />
                                </FormBase>
                            </FormProvider>
                        </RegistrationModal>
                    ) : (
                        <SendMessage
                            title={modalConfig.config?.title || ''}
                            description={modalConfig.config?.description || ''}
                            buttonText={modalConfig.config?.buttonText || ''}
                            action={() => modalConfig.config?.action()}
                        />
                    )}
                </ModalBody>
            </Modal>
        );
    }
);

export default HandbookRegisterModal;
